import { bookingSortKey, isBookingUpcoming } from "@/lib/format";
import type { Booking } from "@/types/doctor";

/**
 * How a booking is filed in the UI.
 *
 * All four buckets now map 1:1 onto the backend's `BookingStatus`
 * (backend_update.md §4 made `missed` a real, swept status), so this is a
 * display vocabulary rather than a derivation.
 */
export type BookingView = "booked" | "completed" | "missed" | "cancelled";

export const BOOKING_VIEWS: BookingView[] = ["booked", "completed", "missed", "cancelled"];

/**
 * The server's status is authoritative and is taken as-is wherever it is set.
 *
 * The one thing left to resolve locally is the **sweep window**: the backend
 * flips `booked → missed` on a job that runs every 5 minutes, so for up to five
 * minutes after a slot ends the row still says `booked`. Reading the clock
 * there keeps the lists honest without contradicting the server — it reaches
 * the same verdict the sweep is about to, using the same rule (slot end passed,
 * patient never joined), just sooner.
 *
 * `patientJoinedAt` also covers the case no job can: a call the patient
 * attended but which was never ended cleanly (tab closed, network dropped), so
 * the booking never got flipped to `completed` and the sweep will never call it
 * missed. That row would otherwise sit in Booked forever.
 */
export function resolveBookingView(booking: Booking, now: Date): BookingView {
  if (booking.status === "cancelled") return "cancelled";
  if (booking.status === "completed") return "completed";
  if (booking.status === "missed") return "missed";

  // status === "booked" from here on.
  if (isBookingUpcoming(booking, now)) return "booked";
  return booking.patientJoinedAt ? "completed" : "missed";
}

/** True while the consultation could still happen — the only "live" bucket. */
export const isActiveBooking = (view: BookingView) => view === "booked";

/**
 * Whether a call can still be started for this booking.
 *
 * Mirrors what the backend actually enforces: `/call/initiate` rejects
 * cancelled and completed bookings. A **missed** one is deliberately still
 * callable — a late patient is common, and the doctor should be able to
 * connect right up until the appointment is closed for real.
 *
 * Shared so the booking card's button and the call screen's gate cannot drift
 * into disagreeing about the same booking.
 */
export const isCallableBooking = (booking: Booking) =>
  booking.status === "booked" || booking.status === "missed";

/**
 * Booked reads soonest-first: it is a to-do list. Every other bucket is
 * history, so it reads newest-first.
 */
export function compareBookings(a: Booking, b: Booking, view: BookingView | "all"): number {
  if (view === "booked") return bookingSortKey(a) - bookingSortKey(b);
  return bookingSortKey(b) - bookingSortKey(a);
}
